import BackendService from 'BackendService';
import TokenData from 'TokenData';
import ContentManagementInterface, {
  ImageCarouselType,
} from '../models/ContentManagementInterface';
import { isDocumentPageRasterPath } from './documentPageRasterPath';
import { BulkFetchService } from './BulkFetchService';

export const placeholderImageItemContentItem: ImageCarouselType = {
  type: 'ImageCarousel',
  id: '0',
  properties: {
    id: '1',
    heading: { id: '2', content: 'Coming Soon' },
    items: [
      {
        heading: { id: '3', content: 'Coming Soon' },
        text: { id: '4', content: 'Details coming soon' },
        imageUrl: {
          id: '5',
          url: '/assets/coming_soon.jpg',
        },
        summaryImage: true,
        altText: { id: '6', content: 'Coming Soon' },
      },
    ],
  },
};

export default class FundOpportunityContentCacheImpl {
  private contentCache = new Map<string, ContentManagementInterface | null>();
  private inFlightContent = new Map<string, Promise<ContentManagementInterface | null>>();
  private imageCache = new Map<string, string>();
  private bulkFetchService: BulkFetchService;
  private backendService: BackendService;
  private assetStorageToken?: TokenData;

  constructor(assetStorageToken?: TokenData) {
    this.assetStorageToken = assetStorageToken;
    // Initialize with default services first
    this.backendService = new BackendService();
    this.bulkFetchService = new BulkFetchService(this.backendService);
    // Then apply token override if provided
    this.initializeServices();
  }

  /**
   * Inject an asset storage token to use for all backend calls
   * This prevents the need to call setupAssetStorage which requires authentication
   */
  setAssetStorageToken(token: TokenData): void {
    this.assetStorageToken = token;
    this.initializeServices();
  }

  /**
   * Clear the asset storage token and revert to normal backend authentication
   */
  clearAssetStorageToken(): void {
    this.assetStorageToken = undefined;
    this.initializeServices();
  }

  private initializeServices(): void {
    // Create a BackendService instance with token override if provided
    this.backendService = new BackendService();
    if (this.assetStorageToken) {
      const token = this.assetStorageToken;
      (this.backendService as any).setupAssetStorageWithRetry = (): Promise<TokenData> => {
        return Promise.resolve(token);
      };
    }

    // Create BulkFetchService with the custom BackendService
    this.bulkFetchService = new BulkFetchService(this.backendService);
  }

  get placeHolderContent(): ContentManagementInterface {
    return {
      name: 'Coming Soon',
      relatedObject: 'FundOpportunity',
      relatedObjectId: 'Coming Soon',
      content: [placeholderImageItemContentItem],
    };
  }

  getContent(
    fundOpportunityId: string,
    type: 'FundOpportunity' | 'FundUpdate' = 'FundOpportunity',
    _ignore?: boolean, // added for consistency
    _ignore2?: boolean, // addedd for consistency
  ): Promise<ContentManagementInterface | null> {
    const cacheKey = `${type}-${fundOpportunityId}`;
    const noResultResult = type === 'FundOpportunity' ? this.placeHolderContent : null;

    if (this.contentCache.has(cacheKey)) {
      const cached = this.contentCache.get(cacheKey);
      if (cached) {
        return Promise.resolve(cached);
      }
      this.contentCache.delete(cacheKey);
    }

    // Callers asking again while the first fetch is still running (e.g. a card list re-rendering
    // as each fund's content arrives) share it rather than starting another download.
    const inFlight = this.inFlightContent.get(cacheKey);
    if (inFlight) return inFlight;

    // Use bulk fetch service instead of immediate individual fetch
    const contentPath = `contentManagement/${type}/${fundOpportunityId}/published.json`;

    const request = this.bulkFetchService
      .queueFetch(cacheKey, contentPath)
      .then((content) => {
        if (content) {
          const parsed = JSON.parse(content) as ContentManagementInterface;
          this.contentCache.set(cacheKey, parsed);
          return parsed;
        }
        return noResultResult;
      })
      .catch(() => noResultResult)
      .finally(() => this.inFlightContent.delete(cacheKey));
    this.inFlightContent.set(cacheKey, request);
    return request;
  }

  getAssetImageUrl(path: string): Promise<string> {
    path = path.replace(/^\/api\//, '');

    if (this.imageCache.has(path)) {
      return Promise.resolve(this.imageCache.get(path)!);
    }

    return this.resolveAssetImageUrl(path);
  }

  private async resolveAssetImageUrl(path: string): Promise<string> {
    // Page rasters have no GCS thumbnail; fetch the PNG API once.
    if (isDocumentPageRasterPath(path)) {
      return this.loadFullImage(path);
    }

    const thumbnailPath = this.getThumbnailPath(path);

    try {
      const previewUrl = await this.backendService.getAssetByURL(thumbnailPath, { url: true });
      if (typeof previewUrl === 'string') {
        console.log(`Found preview for: ${path}`);
        this.imageCache.set(path, previewUrl);
        setTimeout(() => {
          this.loadFullImage(path).catch((err) => {
            console.log(`Error loading full image for ${path}:`, err);
          });
        }, 30000);
        return previewUrl;
      }
    } catch {
      // Preview not found, load full image immediately
    }

    return this.loadFullImage(path);
  }

  private getThumbnailPath(path: string): string {
    const lastDotIndex = path.lastIndexOf('.');
    if (lastDotIndex === -1) {
      return `${path}-thumbnail`;
    }
    const basePath = path.substring(0, lastDotIndex);
    const extension = path.substring(lastDotIndex);
    return `${basePath}-thumbnail${extension}`;
  }

  private getPreviewPath(path: string): string {
    const lastDotIndex = path.lastIndexOf('.');
    if (lastDotIndex === -1) {
      return `${path}-preview`;
    }
    const basePath = path.substring(0, lastDotIndex);
    const extension = path.substring(lastDotIndex);
    return `${basePath}-preview${extension}`;
  }

  private async loadFullImage(path: string): Promise<string> {
    try {
      const url = await this.backendService.getAssetByURL(path, { url: true });
      if (typeof url === 'string') {
        this.imageCache.set(path, url);
        return url;
      }
    } catch (error) {
      if (isDocumentPageRasterPath(path)) {
        throw error;
      }
    }
    this.imageCache.set(path, '/assets/coming_soon.jpg');
    return '/assets/coming_soon.jpg';
  }

  clearCache(
    fundOpportunityId: string,
    type: 'FundOpportunity' | 'FundUpdate' = 'FundOpportunity',
  ): void {
    const cacheKey = `${type}-${fundOpportunityId}`;
    this.contentCache.delete(cacheKey);
  }
}
